import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { Crosswalk, CrosswalkInput } from '@kinetic-ui/data-layer';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCrosswalkForm, newCrosswalkValues } from './useCrosswalkForm';
import useCrosswalkStyles from '../styles';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { CROSSWALKS_FORM_ID } from '../../../const';
import { LocationFormContext } from '../../../context';
import { nanoid } from 'nanoid';
import { useSelectedCrosswalk } from '../useSelectedCrosswalk';
import { Alert } from '@mui/material';
import { KineticForm, renderView } from 'components/forms';
import { useRouteBuilder } from 'kineticUtils/hooks';
import { LOCATIONS_MODULE_ROUTE } from 'const/routes';

interface CrosswalkFormProps {
  isNew?: boolean;
  onClose?: () => void;
}

export const CrosswalkForm: React.FunctionComponent<CrosswalkFormProps> = ({ isNew, onClose }) => {
  const { t } = useTranslation(['locations', 'common']);
  const classes = useCrosswalkStyles();
  const route = useRouteBuilder(LOCATIONS_MODULE_ROUTE);
  const { setValues: setLocationFormValues, setDirty, disabled } = useContext(LocationFormContext);
  const { selectedCrosswalk, setSelectedCrosswalk } = useSelectedCrosswalk();

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const initalData = selectedCrosswalk
    ? {
        ...newCrosswalkValues,
        id: selectedCrosswalk.id,
        geometry: selectedCrosswalk.geometry,
      }
    : newCrosswalkValues;

  const formData = isNew ? initalData : selectedCrosswalk;

  const { errors, formProps, values, isSaveDisabled, setDirtyFields, viewProps } = useCrosswalkForm(
    formData as Partial<CrosswalkInput> | undefined,
    disabled
  );

  const [formValues, setValues, reset] = values;
  const [hasErrors] = errors;

  useEffect(() => {
    setValues((prev) => ({
      ...prev,
      geometry: selectedCrosswalk?.geometry,
    }));
    setDirtyFields((prev: string[]) => [...prev, 'location']);
  }, [selectedCrosswalk?.geometry, setDirtyFields, setValues]);

  useEffect(() => {
    if (selectedCrosswalk?.id !== formValues.id) {
      reset({ ...selectedCrosswalk, description: selectedCrosswalk?.description ?? undefined });
      setDirtyFields([]);
    }
  }, [selectedCrosswalk?.id, formValues.id, selectedCrosswalk, reset, setDirtyFields, setValues]);

  const handleUpdate = useCallback(
    (crosswalk: CrosswalkInput) => {
      setDirty(CROSSWALKS_FORM_ID, true);

      setLocationFormValues((prev) => {
        const index = prev?.crosswalks?.findIndex((found) => found.id === crosswalk.id) ?? 0;
        const newCrosswalks = prev?.crosswalks?.filter((found) => found.id !== crosswalk.id) ?? [];
        newCrosswalks.splice(index, 0, crosswalk);
        return {
          ...prev,
          crosswalks: newCrosswalks,
        };
      });

      setSelectedCrosswalk((prev) => {
        return { ...prev, ...crosswalk };
      });
    },
    [setDirty, setLocationFormValues, setSelectedCrosswalk]
  );

  const handleAdd = useCallback(
    (crosswalk: Crosswalk) => {
      const newId = nanoid();
      const newCrosswalk: Crosswalk = { ...crosswalk, id: newId };

      setDirty(CROSSWALKS_FORM_ID, true);
      setLocationFormValues((prev) => {
        return {
          ...prev,
          crosswalks: [...(prev.crosswalks ?? []), newCrosswalk],
        };
      });
      onClose?.();
    },
    [onClose, setDirty, setLocationFormValues]
  );

  const handleRemove = useCallback(
    (id: string) => {
      setDirty(CROSSWALKS_FORM_ID, true);
      setLocationFormValues((prev) => {
        return {
          ...prev,
          crosswalks: prev?.crosswalks?.filter((search) => search.id !== id) ?? [],
        };
      });
      setSelectedCrosswalk(undefined);
      onClose?.();
    },
    [setDirty, setSelectedCrosswalk, setLocationFormValues, onClose]
  );

  const handleSave = () => {
    if (hasErrors) {
      return;
    }
    const toSave = formValues as CrosswalkInput;
    toSave.geometry = selectedCrosswalk?.geometry;

    isNew ? handleAdd(toSave) : handleUpdate(toSave);
    reset();
    onClose?.();
  };

  const handleCancel = () => {
    reset();
    onClose?.();
  };

  return (
    <div>
      <Alert severity='info' sx={{ mt: 2 }}>
        {isNew ? t('crosswalkInstructions') : t('crosswalkUpdateInstructions')}
      </Alert>

      <KineticForm {...formProps} flexDirection='column'>
        {({ fieldComponents }) => renderView(viewProps, fieldComponents, route)}
      </KineticForm>

      <div className={classes.actionButtons}>
        <div>
          {!isNew && (
            <IconButton disabled={disabled} onClick={() => setDeleteConfirmOpen(true)}>
              <DeleteIcon />
            </IconButton>
          )}
        </div>
        <div>
          <Button
            disabled={disabled}
            className={classes.actionButton}
            onClick={handleCancel}
            color='secondary'
          >
            {t('common:cancel')}
          </Button>
          <Button
            className={classes.actionButton}
            disabled={isSaveDisabled || disabled}
            onClick={handleSave}
            variant='contained'
          >
            {isNew ? t('common:create') : t('common:update')}
          </Button>
        </div>
      </div>

      <ConfirmDeleteDialog
        crosswalk={selectedCrosswalk}
        open={deleteConfirmOpen}
        setOpen={setDeleteConfirmOpen}
        onDelete={(id) => handleRemove(id)}
      />
    </div>
  );
};
